import React, { useState, useEffect, useRef, useCallback } from "react";
import Sidebar from "../../../components/sidebar";
import { useOutletContext } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import { Button } from "react-bootstrap";
import "./style.scss";
import SearchIcon from "../../../icons/SearchIcon";
import ThreeDotsIcon from "../../../icons/ThreeDotsIcon";
import EditGreyIcon from "../../../icons/EditGreyIcon";
import ArchiveGreyIcon from "../../../icons/ArchiveGreyIcon";
import ActivityModal from "../../../core/components/modals/activityModal";
import { CommonPagination } from "../../../core/components/common/CommnPagination";
import { useDispatch, useSelector } from "react-redux";
import { getActivityList } from "../../../core/data/redux/activity/_activityAction";
import { logHourFormatTime } from "../../../constants/date";
import { DeleteModalType } from "../../../constants/utlis";
import DeleteModal from "../../../components/deleteModal";
import ArchiveBlackIcon from "../../../icons/ArchiveBlackIcon";
import { apiPageLimit } from "../../../constants/common";
import PlusIcon from "../../../icons/PlusIcon";

const NonRealActivity: React.FC = () => {
  const { isSidebarOpen } = useOutletContext<{
    isSidebarOpen: boolean;
    onToggleSidebar: () => void;
  }>();
  const dispatch: any = useDispatch();

  const activityDetails = useSelector((state: any) => state?.activity);
  const activityData = activityDetails?.activityList || [];
  const totalCount = activityDetails?.totalCount || 0;

  const [searchTerm, setSearchTerm] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const pageSize = apiPageLimit;

  const [activityType, setActivityType] = useState<string>("");
  const [participationLevel, setParticipationLevel] = useState<string>("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const [modalType, setModalType] = useState<DeleteModalType>("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUserUuid, setSelectedUserUuid] = useState<string | null>(null);
  const [showActivityModal, setShowActivityModal] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fetch activity list on component mount
  useEffect(() => {
    dispatch(getActivityList({}));
  }, [dispatch]);

  // Debounce search
  const debounce = (func: (...args: any[]) => void, delay: number) => {
    let timer: NodeJS.Timeout;
    return (...args: any[]) => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        func(...args);
      }, delay);
    };
  };

  // Fetch activity with debounce
  const fetchActivity = useCallback(
    debounce(
      (
        search: string,
        page: number,
        activityType: string,
        participationLevel: string
      ) => {
        const params: any = { page, limit: pageSize };
        if (search) params.search = search;
        if (activityType) params.activity_type = activityType;
        if (participationLevel) params.participation_level = participationLevel;
        dispatch(getActivityList(params));
      },
      500
    ),
    [dispatch]
  );

  // Run when searchTerm or selected changes
  useEffect(() => {
    fetchActivity(searchTerm, page, activityType, participationLevel);
  }, [searchTerm, page, activityType, participationLevel, fetchActivity]);

  const totalPages = Math.ceil(totalCount / pageSize);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleOpenDeleteModal = (type: DeleteModalType, uuid: string) => {
    setModalType(type);
    setSelectedUserUuid(uuid);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedUserUuid(null);
  };

  const handleOpenActivityModal = (uuid: string) => {
    setSelectedUserUuid(uuid);
    setShowActivityModal(true);
  };

  const handleCloseActivityModal = () => {
    setShowActivityModal(false);
    setSelectedUserUuid(null);
  };

  const handleClearAllFilters = () => {
    setActivityType("");
    setSearchTerm("");
    setParticipationLevel("");
  };

  return (
    <div className="layout">
      <Sidebar isOpen={isSidebarOpen} />

      <main className="content">
        <div className="profileSettings activities-page-section">
          <div className="rounded activities-page">
            <div className="add-properties-heading flex-wrap d-flex justify-content-between align-items-center gap-3 mb-4">
              <h2 className="mb-0">Activities</h2>
              <Button
                className="log-time-button btn primaryButton  btn-primary d-inline-flex justify-content-center align-items-center  btn-block"
                variant="primary"
                onClick={(event) => {
                  handleOpenActivityModal("");
                }}
              >
                <PlusIcon /> Add Activity
              </Button>
            </div>

            <div className="row g-3 mb-3 mt-3">
              <div className="col-sm-12">
                <div className="row-boder-line p-3">
                  <div className="row">
                    {/* Search log hours */}
                    <div className="col-12 col-sm-12 col-md-12 col-lg-12 col-xl-4 col-xxl-7 mb-1 mt-1">
                      <div className="input-group commonInput bg-light bg-white">
                        <span className="input-group-text bg-white border-end-0 ">
                          <SearchIcon />
                        </span>
                        <input
                          type="text"
                          placeholder="Search activities by name or category"
                          className="name form-control border-start-0"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </div>
                    </div>

                    {/* Activity Type */}
                    <div className="col-12 col-sm-12 col-md-12 col-lg-12 col-xl-3 col-xxl-2 mb-1 mt-1">
                      <select
                        className="form-select form-control commonInput"
                        value={activityType}
                        onChange={(e) => setActivityType(e.target.value)}
                      >
                        <option value="" disabled selected hidden>
                          Activity Type
                        </option>
                        <option value="">All</option>
                        <option value="business">Business</option>
                        <option value="investment">Investment</option>
                        <option value="trading">Trading</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    {/* Status select */}
                    <div className="col-12 col-sm-12 col-md-12 col-lg-12 col-xl-3 col-xxl-2 mb-1 mt-1">
                      <select
                        className="form-select form-control commonInput"
                        value={participationLevel}
                        onChange={(e) => setParticipationLevel(e.target.value)}
                      >
                        <option value="" disabled selected hidden>
                          Participation Level
                        </option>
                        <option value="">Both</option>
                        <option value="active">Active</option>
                        <option value="passive">Passive</option>
                      </select>
                    </div>
                    <div className="col-12 col-sm-12 col-md-12 col-lg-12 col-xl-2 col-xxl-1 mb-1 mt-1">
                      <div className="clear-all-button">
                        <button onClick={() => handleClearAllFilters()}>
                          Clear All
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* row-end */}
            <div className="row g-3 mt-3 mb-3 pb-3">
              <div className="col-sm-12">
                <div className="row-boder-line">
                  <div
                    className={`grid-container mt-0 ${
                      activityData?.length > 0 ? "" : "noData"
                    }`}
                  >
                    {activityData?.length > 0 ? (
                      activityData?.map((activity: any) => {
                        return (
                          <div className="box ">
                            <div className="custom-card mx-auto">
                              <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center flex-wrap">
                                  <h5 className="mb-0 fw-bold">
                                    {activity?.name}
                                  </h5>

                                  <div className="dropdown">
                                    <button
                                      className="three-dot-btn rotate90"
                                      type="button"
                                      data-bs-toggle="dropdown"
                                      aria-expanded="false"
                                    >
                                      <ThreeDotsIcon />
                                    </button>

                                    <ul className="dropdown-menu dropdown-menu-end">
                                      <li
                                        onClick={(event) => {
                                          event.stopPropagation(); // Prevent click from bubbling to parent
                                          handleOpenActivityModal(
                                            activity?.uuid
                                          );
                                        }}
                                      >
                                        <a className="dropdown-item" href="#">
                                          <EditGreyIcon />
                                          Edit
                                        </a>
                                      </li>
                                      <li
                                        onClick={(event) => {
                                          event.stopPropagation(); // Prevent click from bubbling to parent
                                          handleOpenDeleteModal(
                                            "archiveActivity",
                                            activity?.uuid
                                          );
                                        }}
                                      >
                                        <a className="dropdown-item " href="#">
                                          <ArchiveBlackIcon />
                                          Archive
                                        </a>
                                      </li>
                                    </ul>
                                  </div>
                                </div>

                                <div className="d-flex gap-2 mt-2 align-items-center flex-wrap">
                                  <span className="category-badge tag-sky-blue">
                                    {activity?.type}
                                  </span>
                                  <span
                                    className={
                                      activity?.participation_level.toLowerCase() ==
                                      "active"
                                        ? "category-badge"
                                        : "category-badge tag-red"
                                    }
                                  >
                                    {activity?.participation_level}
                                  </span>
                                </div>

                                <div className="visit-text mt-3 mb-3 text-left">
                                  <p className="text-black text-left ">
                                    {activity?.description}
                                  </p>
                                </div>
                              </div>

                              <div className="status-row d-flex justify-content-between align-items-center flex-wrap">
                                <div className="sun text-left">
                                  <span className="tag text-black text-left ">
                                    {" "}
                                    Tax Category:{" "}
                                  </span>
                                  <span
                                    className={
                                      activity?.participation_level.toLowerCase() ==
                                      "active"
                                        ? "tag tag-red mb-2"
                                        : "tag tag-blue"
                                    }
                                  >
                                    {activity?.tax_category}
                                  </span>
                                </div>

                                {activity?.total_time_spent?.total_minutes >
                                  0 && (
                                  <span className="time-badge">
                                    {logHourFormatTime({
                                      hours: activity?.total_time_spent?.hours,
                                      minutes:
                                        activity?.total_time_spent?.minutes,
                                    })}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <p className="text-center w-100 py-4 noFound">
                        No activity found.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Pagination */}
            <CommonPagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          </div>
        </div>
      </main>

      {/* Activity Modal */}
      <ActivityModal
        show={showActivityModal}
        onHide={handleCloseActivityModal}
        uuid={selectedUserUuid}
        // onSuccess={}
      />

      {/* Delete Modal */}
      <DeleteModal
        show={showDeleteModal}
        onHide={handleCloseDeleteModal}
        type={modalType}
        uuid={selectedUserUuid}
      />
    </div>
  );
};

export default NonRealActivity;
